import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Typography,
  Table,
  ModalVariables
} from '../../../components';
import affiliateService from '../../../api/affiliateService';
import { icons } from '../../../icons';
import { useTranslation } from 'react-i18next';
import { useUi } from '../../../context/ui.context';
import { usePageTitle } from '../../../context/navigationContext';

// Types for the affiliate settings
type AffiliateSettings = {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  manual_referrals_accept: boolean;
  signup_bonus: boolean;
  signup_bonus_value: string;
  percent_payment: string;
  created_at: string;
  updated_at: string;
};

type ApiResponse = {
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  results: AffiliateSettings[];
};

const AffiliateSettings = () => {
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [data, setData] = useState<ApiResponse | null>(null);
  const [viewModal, setViewModal] = useState(false);
  const [activeAnimation, setActiveAnimation] = useState(false);
  const [modalType, setModalType] = useState<string>('');
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertSubTitle, setAlertSubTitle] = useState<any>('');
  const [deletedSetting, setDeletedSetting] = useState<AffiliateSettings | null>(null);
  const navigate = useNavigate();
  const [t] = useTranslation();
  usePageTitle(t("PAGE_TITLE_AFFILIATE"));
  const { viewAlert } = useUi();

  // Load data
  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await affiliateService.getSettings();
      setData(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      viewAlert({
        severity: 'error',
        message: 'Failed to load settings.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Open delete modal
  const openDeleteModal = (setting: AffiliateSettings) => {
    console.log('Opening delete modal for setting:', setting); // Debug log
    setDeletedSetting(setting);
    setAlertTitle('Affiliate Setting');
    setAlertSubTitle([setting]);
    setModalType('delete');
    setViewModal(true);
    setActiveAnimation(true);
  };

  // Delete setting (called from modal)
  const handleDelete = async () => {
    if (!deletedSetting) return;
    
    setDeleteLoading(true);
    try {
      await affiliateService.deleteSettings(deletedSetting.id);
      viewAlert({
        severity: 'information',
        message: 'Setting deleted successfully!'
      });
      loadSettings(); // Reload data
    } catch (error) {
      console.error('Error deleting setting:', error);
      viewAlert({
        severity: 'error',
        message: 'Failed to delete setting.'
      });
    } finally {
      setDeleteLoading(false);
      setViewModal(false);
      setActiveAnimation(false);
      setDeletedSetting(null);
    }
  };

  // Close modal
  const closeModal = () => {
    if (modalType === 'delete') {
      handleDelete();
    }
  };

  const closeModalCancel = () => {
    setViewModal(false);
    setActiveAnimation(false);
    setDeletedSetting(null);
  };

  useEffect(() => {
    loadSettings();
  }, []);



  // Table columns configuration
  const columns = [
    {
      title: 'Name',
      key: 'name',
    },
    {
      title: 'Description',
      key: 'description',
    },
    {
      title: 'Default',
      key: 'is_default',
    },
    {
      title: 'Active',
      key: 'is_active',
    },
    {
      title: 'Manual Accept',
      key: 'manual_referrals_accept',
    },
    {
      title: 'Signup Bonus',
      key: 'signup_bonus',
    },
    {
      title: 'Bonus Value',
      key: 'signup_bonus_value',
    },
    {
      title: 'Payment %',
      key: 'percent_payment',
    },
    {
      title: '',
      key: 'props',
    },
  ];

  // Settings for dropdown menu (similar to users example)
  const getSettingsForItem = (item: any) => {
    const baseSettings:any = [
      {
        title: 'View Details',
        id: 'view',
        icon: icons.EyeIc,
        onClick: () => navigate(`/billing/referrals/view/${item.id}`),
        divider: false
      },
      {
        title: 'Edit',
        id: 'edit',
        icon: icons.Edit,
        onClick: () => navigate(`/billing/referrals/edit/${item.id}`),
        divider: false
      }
    ];

    if (!item.is_default) {
      baseSettings.push({
        title: 'Delete',
        id: 'delete',
        icon: icons.DeleteIcon,
        onClick: () => {
          console.log('Delete clicked for item:', item); // Debug log
          openDeleteModal(item);
        },
      });
    }

    return baseSettings;
  };

  // Format table rows
  const formatTableRows = (settings: AffiliateSettings[]) => {
    return settings.map((setting) => ({
      ...setting,
      name: (
        <div 
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/billing/referrals/view/${setting.id}`)}
        >
          {setting.name}
        </div>
      ),
      is_default: setting.is_default ? 'Yes' : 'No',
      is_active: setting.is_active ? 'Yes' : 'No',
      manual_referrals_accept: setting.manual_referrals_accept ? 'Yes' : 'No',
      signup_bonus: setting.signup_bonus ? 'Yes' : 'No',
      signup_bonus_value: `$${setting.signup_bonus_value}`,
      percent_payment: `${setting.percent_payment}%`,
      // Add individual settings for each row
      dropOptions: getSettingsForItem(setting),
    }));
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <Typography variant="h1">
          Affiliate Settings
        </Typography>
        <Button
          title="Create New"
          onClick={() => navigate('/billing/referrals/create')}
          variant="primary"
          size="mid"
        />
      </div>


      <Table
        columns={columns}
        rows={data ? formatTableRows(data.results) : []}
        loading={loading}
        paginationData={{
          count: data?.count || 0,
          total_pages: data?.total_pages || 1,
          current_page: data?.current_page || 1,
          page_size: data?.page_size || 10,
        }}
        updateData={loadSettings}
        hideCheckbox={true}
      />

      <ModalVariables
        modalType={modalType as any}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={deleteLoading}
      />
    </div>
  );
};

export default AffiliateSettings;